import type { IChecklist } from 'src/packages/checklistManagement';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { MachineModel } from './../../../../../../app/models/machine';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { OrderModel } from './../../../../../../app/models/order';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- app-boundary-backlog
import { GalleryAdapter } from './../../../../../../app/pages/processing/orders/order-details/writeable-checklist-details/writeable-checklist-module/GalleryAdapter';
import { IChecklistDependency } from './../../../../../checklistManagement/models/checklist-dependency';
import { IChecklistGroup } from './../../../../../checklistManagement/models/checklist-group';
import { IChecklistModule } from './../../../../../checklistManagement/models/checklist-module';
import { IMitsPDF } from './../../../../interfaces/mits-pdf';
import { ImageGenerator } from './imageGenerator';

/**
 * Hilfsklasse zum Transformieren von Checklisten
 */
export class ChecklistGenerator {
  private checklist: IChecklist;
  private machine?: MachineModel;
  private order: OrderModel;
  private docDef: IMitsPDF;

  constructor(
    private readonly galleryAdapter: GalleryAdapter,
    private readonly imageGenerator: ImageGenerator,
    docDef: IMitsPDF,
    checklist: IChecklist,
    order: OrderModel,
    machine?: MachineModel
  ) {
    this.docDef = docDef;
    this.checklist = checklist;
    this.order = order;
    this.machine = machine;
  }

  public async getChecklistPDFContent(): Promise<any[]> {
    const ddContent = [];
    ddContent.push(
      {
        columns: [
          {
            text: this.checklist.name,
            fontSize: '22',
            alignment: 'right',
            margin: [0, 8, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            text: this.checklist.created_at
              ? 'Datum: ' + this.formatDate(this.checklist.created_at)
              : '',
            fontSize: '12',
            alignment: 'right',
          },
        ],
      },
      this.order
        ? {
            columns: [
              {
                text: 'Auftragsdaten',
                fontSize: '14',
                alignment: 'left',
                bold: true,
              },
            ],
            margin: [0, 16, 0, 0],
          }
        : {},
      this.order ? this.getOrderHeader() : {},
      this.order ? this.getOrderHeaderData() : {},
      this.machine
        ? {
            columns: [
              {
                text: 'Gerätedaten',
                fontSize: '14',
                alignment: 'left',
                bold: true,
              },
            ],
            margin: [0, 16, 0, 0],
          }
        : {},
      this.machine ? this.getMachineHeader() : {},
      this.machine ? this.getMachineHeaderData() : {}
    );
    const promisesGroupContent: Promise<any[]>[] = [];
    this.checklist.groups.forEach((group) => {
      promisesGroupContent.push(this.generateGroupContent(group));
    });
    const promiseData = await Promise.all(promisesGroupContent);
    promiseData.forEach((gc) => ddContent.push(gc));
    return ddContent;
  }

  private getOrderHeader(): any {
    return {
      columns: [
        {
          text: 'Auftrag Nr.',
          fontSize: '12',
          alignment: 'left',
        },
        {
          text: 'Kunde',
          fontSize: '12',
          alignment: 'left',
        },
        {
          text: 'Kunden-Nr.',
          fontSize: '12',
          alignment: 'left',
        },
      ],
      margin: [0, 4, 0, 0],
    };
  }

  private getOrderHeaderData(): any {
    return {
      columns: [
        {
          text: this.order.number,
          fontSize: '12',
          alignment: 'left',
        },
        {
          text: this.order.customer.name1,
          fontSize: '12',
          alignment: 'left',
        },
        {
          text: this.order.customer.snc_number,
          fontSize: '12',
          alignment: 'left',
        },
      ],
    };
  }

  private getMachineHeader(): any {
    return {
      columns: [
        {
          text: 'Gerät Nr.',
          fontSize: '12',
          alignment: 'left',
        },
        {
          text: 'Seriennummer',
          fontSize: '12',
          alignment: 'left',
        },
      ],
      margin: [0, 4, 0, 0],
    };
  }

  private getMachineHeaderData(): any {
    return {
      columns: [
        {
          text: this.machine?.inventory_number,
          fontSize: '12',
          alignment: 'left',
        },
        {
          text: this.machine?.serial_number,
          fontSize: '12',
          alignment: 'left',
        },
      ],
    };
  }

  /**
   * Datum als dd.mm.yyyy formatieren
   * @param date to format
   * @returns formatted date or empty string for invalid dates
   */
  private formatDate(date: Date | string): string {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '';
    }
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  }

  private async generateGroupContent(g: IChecklistGroup): Promise<any[]> {
    const items = await this.getGroupModulesTableItems(g.modules);
    return [
      {
        columns: [
          {
            text: g.title,
            fontSize: '16',
            alignment: 'left',
            bold: true,
          },
        ],
        margin: [0, 8, 0, 0],
      },
      {
        layout: 'lightHorizontalLines',
        table: {
          headerRows: 1,
          widths: [30, 200, '*'],
          body: [
            [
              { text: 'Pos.', bold: true },
              { text: 'Bezeichnung', bold: true },
              { text: 'Eingabe', bold: true },
            ],
          ].concat(items),
        },
      },
    ];
  }

  private async getGroupModulesTableItems(
    modules: IChecklistModule[]
  ): Promise<any[]> {
    let listedModules: unknown[] = [];
    const promisesModulesItems: Promise<any[][]>[] = [];
    modules.forEach((m, mIndex) => {
      promisesModulesItems.push(this.getModuleTableItems(m, undefined, mIndex + 1));
    });
    const modulesItemsData = await Promise.all(promisesModulesItems);
    modulesItemsData.forEach((mid) => {
      listedModules = listedModules.concat(mid);
    });
    return listedModules;
  }

  private async getModuleTableItems(
    m: IChecklistModule,
    preIndex?: string,
    currentIndex: number = 1,
    items: any[][] = []
  ): Promise<any[][]> {
    preIndex = preIndex
      ? preIndex + '.' + currentIndex
      : currentIndex.toString();
    const itemContent = await this.getModuleContent(m);
    items.push([preIndex, m.title, itemContent]);
    if (m.dep && m.dep.length > 0) {
      // tslint:disable-next-line: prefer-for-of
      for (let j = 0; j < m.dep.length; j++) {
        const d = m.dep[j];

        if (this.checkWithRegEx(d, m)) {
          for (let i = 0; i < d.modules.length; i++) {
            const dm = d.modules[i];
            await this.getModuleTableItems(dm, preIndex, i + 1, items);
          }
        }
      }
    }
    return items;
  }

  private async getModuleContent(m: IChecklistModule): Promise<any> {
    if (m.type === 'ChecklistManagement::Modules::BooleanModule') {
      return m.value === 'true' ? 'Ja' : 'Nein';
    } else if (m.type === 'ChecklistManagement::Modules::GalleryModule') {
      const images = await this.galleryAdapter.loadImagesDirect(
        m.gallery_client_id ?? m.id
      );
      const data: { columns: unknown[] } = {
        columns: [],
      };
      // tslint:disable-next-line: prefer-for-of
      for (let i = 0; i < images.length; i++) {
        const img = await this.getDataURLOfBlobURL(
          images[i]?.file_url?.toString() ?? ''
        );
        if (!img) {
          continue;
        }
        data.columns.push({
          image: this.imageGenerator.attachImage(img),
          width: 100,
        });
      }
      return data;
    } else {
      return m.value ? m.value : '';
    }
  }

  private checkWithRegEx(
    d: IChecklistDependency,
    m: IChecklistModule
  ): boolean {
    if (m.value == null) return false;
    const regExMatches = m.value.toString().match(d.value);
    return !!regExMatches && regExMatches.length > 0;
  }

  private async getDataURLOfBlobURL(blobUrl: string): Promise<string> {
    return new Promise((resolve) => {
      if (!blobUrl) {
        resolve('');
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.timeout = 15000;
      xhr.onload = () => {
        // blob:-URLs liefern Status 0 oder 200; alles andere ist ein Fehler
        if (xhr.status && xhr.status !== 200) {
          resolve('');
          return;
        }
        const recoveredBlob = xhr.response;
        const reader = new FileReader();
        reader.onload = () => {
          const blobAsDataUrl = reader.result;
          resolve(blobAsDataUrl ? blobAsDataUrl.toString() : '');
        };
        reader.onerror = () => resolve('');
        reader.readAsDataURL(recoveredBlob);
      };
      xhr.onerror = () => resolve('');
      xhr.ontimeout = () => resolve('');
      xhr.open('GET', blobUrl);
      xhr.send();
    });
  }
}
